export type AppVersionUpdate = {
  latestVersion: string | null;
  shouldPersist: boolean;
  shouldReload: boolean;
};

/** Compare the loaded app version with the latest Firestore setting. */
export function getAppVersionUpdate(currentVersion: string | null, latestVersion: unknown): AppVersionUpdate {
  if (latestVersion === null || latestVersion === undefined) {
    return {
      latestVersion: null,
      shouldPersist: false,
      shouldReload: false,
    };
  }

  const normalizedLatestVersion = String(latestVersion);
  const shouldPersist = currentVersion !== normalizedLatestVersion;

  return {
    latestVersion: normalizedLatestVersion,
    shouldPersist,
    shouldReload: currentVersion !== null && shouldPersist,
  };
}

type AppVersionMonitorDependencies = {
  storage: Pick<Storage, "getItem" | "setItem">;
  getPathname: () => string;
  reloadPage: () => void;
};

/**
 * A version change must not discard an in-memory review (including its mistake
 * selection), editor, or recording. Queue it until a later navigation to a
 * home/deck overview: even overview routes can contain unsaved notes/uploads.
 * Keep the comparison version per tab: localStorage may be updated by another
 * tab while this one still has an older runtime and a pending update.
 */
export function createAppVersionMonitor({ storage, getPathname, reloadPage }: AppVersionMonitorDependencies) {
  let currentVersion: string | null = null;
  try {
    currentVersion = storage.getItem("currentVersion");
  } catch {
    // Storage may be unavailable; a failed write below must never reload-loop.
  }
  let latestVersion: string | null = null;
  let reloadRequested = false;

  function reloadIfReady() {
    if (reloadRequested) return;
    const update = getAppVersionUpdate(currentVersion, latestVersion);
    if (!update.shouldPersist) return;

    const pathname = getPathname();
    const isOverview = pathname === "/" || pathname === "/my-classes" ||
      /^\/(?:folder(?:-overview)?|deck(?:-overview)?)\/[^/]+(?:\/[^/]+)?\/?$/.test(pathname);
    if (update.shouldReload && !isOverview) return;

    try {
      // Do not acknowledge a deferred update until this tab can apply it.
      storage.setItem("currentVersion", update.latestVersion);
    } catch {
      return;
    }
    currentVersion = update.latestVersion;
    if (update.shouldReload) {
      reloadRequested = true;
      reloadPage();
    }
  }

  return {
    receiveVersion(version: unknown) {
      if (version === null || version === undefined) return;
      latestVersion = String(version);
      // Learning the first version does not reload. Subsequent snapshots only
      // queue an update; the router applies it at the next overview navigation.
      if (currentVersion === null) reloadIfReady();
    },
    reloadIfReady,
  };
}
